/**
 * @file RegulationFields.tsx
 * @description «Регламент маркировки» вида товара (phase19.22, W22): выбор из
 * списка + название для «Другой (указать)» + пояснение `RegulationHint`.
 *
 * «Другой» — решение пользователя 2026-09-26: регламент, которого ещё нет в
 * системе (например, ТР ТС 027/2012 для БАД). Название обязательно (сервер —
 * 422 `labeling_regulation_note_required`), хранится справочно; автоматики
 * по нему нет. Контракт `value/onChange` — как у остальных полей формы.
 */

import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { SelectField } from "../../ui/SelectField";
import type { LabelingRegulation } from "../../../lib/api-product-kinds";
import {
  LABELING_REGULATIONS,
  LABELING_REGULATION_META,
  REGULATION_NOTE_MAX,
} from "../../../lib/product-kind-format";
import { RegulationHint } from "./RegulationHint";

const REGULATION_OPTIONS = LABELING_REGULATIONS.map((r) => ({
  value: r,
  label: LABELING_REGULATION_META[r].option,
}));

interface Props {
  regulation: LabelingRegulation;
  note: string;
  noteError?: string;
  onChange: (patch: { labelingRegulation?: LabelingRegulation; labelingRegulationNote?: string }) => void;
  disabled?: boolean;
}

export function RegulationFields({ regulation, note, noteError, onChange, disabled }: Props) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="pk-reg">Регламент маркировки</Label>
      <SelectField
        id="pk-reg"
        ariaLabel="Регламент маркировки"
        value={regulation}
        onChange={(v) => onChange({ labelingRegulation: v as LabelingRegulation })}
        options={REGULATION_OPTIONS}
        disabled={disabled}
      />
      {regulation === "OTHER" ? (
        <div className="mt-1 flex flex-col gap-1.5">
          <Label htmlFor="pk-reg-note">Название регламента *</Label>
          <Input
            id="pk-reg-note"
            value={note}
            maxLength={REGULATION_NOTE_MAX}
            onChange={(e) => onChange({ labelingRegulationNote: e.target.value })}
            placeholder="например, ТР ТС 027/2012 (БАД)"
            aria-invalid={noteError ? true : undefined}
            aria-describedby="pk-reg-hint"
            disabled={disabled}
            className="min-h-12"
          />
          {noteError ? <p className="text-xs text-destructive">{noteError}</p> : null}
        </div>
      ) : null}
      <RegulationHint id="pk-reg-hint" value={regulation} />
    </div>
  );
}
